import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Fiscal → Fechamento ao cliente (22/10/2026): sobe os PDFs de impostos (a IA lê um por vez),
// confere, resolve o painel "O que falta" e envia ao cliente pela conta central, sempre por
// dentro da prévia. "Registrar" grava o mês só para histórico, sem envio.
// "Registrar mês anterior" troca a competência DESTE card (modo registro): só grava, nunca envia.
// Layout (23/10/2026): duas colunas como o /Relatorio_contabil — à esquerda os dados em seções
// planas, à direita a prévia do e-mail ao vivo (GET previa/, só leitura); o envio continua só
// pelo modal de prévia, com confirmação em 2 passos.

const API = process.env.REACT_APP_API_BASE;
const BASE = `${API}/api/fiscal/fechamento-cliente`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const brl = (v) => (v == null || v === '' ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const dataBr = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '—');
const dataHoraBr = (iso) => (iso ? `${dataBr(iso)}${String(iso).length > 15 ? ` ${String(iso).slice(11, 16)}` : ''}` : '—');
const compBr = (comp) => (comp ? `${comp.slice(5, 7)}/${comp.slice(0, 4)}` : '—');
const compDe = (ano, mes) => `${ano}-${String(mes).padStart(2, '0')}`;
const compAnterior = (ano, mes) => (mes === 1 ? compDe(ano - 1, 12) : compDe(ano, mes - 1));
const mesAtual = () => { const d = new Date(); return compDe(d.getFullYear(), d.getMonth() + 1); };
const MAX_MESES_HISTORICO = 24;
const indiceMes = (comp) => (/^\d{4}-\d{2}$/.test(comp || '') ? Number(comp.slice(0, 4)) * 12 + Number(comp.slice(5, 7)) - 1 : null);
// Período da busca do histórico (mesmas regras do backend): '' = válido
const erroPeriodo = (de, ate) => {
  const i = indiceMes(de);
  const f = indiceMes(ate);
  if (i == null || f == null) return 'Informe os meses De e Até.';
  if (i > f) return 'O mês "De" precisa ser igual ou anterior ao mês "Até".';
  if (f > indiceMes(mesAtual())) return 'O período não pode terminar em mês futuro.';
  if (f - i + 1 > MAX_MESES_HISTORICO) return `O período pode ter no máximo ${MAX_MESES_HISTORICO} meses.`;
  return '';
};
const periodoBr = (periodo) => (periodo ? `${compBr(periodo.inicio)} a ${compBr(periodo.fim)}` : '');
const valorTexto = (v) => (v == null ? '' : Number(v).toFixed(2).replace('.', ','));

const TRIBUTOS = ['IRPJ', 'CSLL', 'PIS', 'COFINS', 'ISS', 'INSS', 'IRRF_RET', 'CSRF_RET', 'INSS_RET', 'DAS', 'ICMS'];
const ROTULO_TRIBUTO = {
  IRPJ: 'IRPJ', CSLL: 'CSLL', PIS: 'PIS', COFINS: 'COFINS', ISS: 'ISS', INSS: 'INSS (patronal, segurados e terceiros)',
  IRRF_RET: 'IRRF retido', CSRF_RET: 'CSRF retida', INSS_RET: 'INSS retido', DAS: 'DAS (Simples)', ICMS: 'ICMS',
};
const SITUACAO_DOC = {
  ok: ['ok', 'fa-circle-check', 'lido'], pendente: ['neutro', 'fa-clock', 'aguardando leitura'],
  erro_ia: ['erro', 'fa-robot', 'IA falhou'], outra_empresa: ['erro', 'fa-building-circle-xmark', 'outra empresa'],
  outra_competencia: ['alerta', 'fa-calendar-xmark', 'outra competência'], ilegivel: ['alerta', 'fa-eye-slash', 'ilegível'],
};
const TIPO_DOC = {
  darf: 'DARF', das: 'DAS', guia_iss: 'Guia de ISS', dctfweb: 'DCTFWeb', elalur: 'e-Lalur', elacs: 'e-Lacs',
  decweb: 'Decweb', demonstrativo: 'Demonstrativo', outro: 'Outro',
};
const FONTE_FAT = { monitor: 'Monitor de NFS-e', decweb: 'Decweb (Receita Bruta)', manual: 'Informado manualmente' };
const DCTF_CONFERE = ['IRPJ', 'CSLL', 'PIS', 'COFINS'];
const MAX_MB = 6;
const MAX_DOCS = 20;
const ATRASO_MES_MS = 600; // o seletor de mês dispara a cada mudança: espera antes de carregar
const ATRASO_PREVIA_MS = 800; // prévia ao vivo: espera as ações assentarem antes do GET (é lento)
const LIDOS_VISIVEIS = 5;     // acima disso a lista de PDFs lidos começa recolhida

function Modal({ icone, titulo, sub, onFechar, children, rodape, grande }) {
  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className={`modal-dialog ${grande ? 'modal-xl' : ''} modal-dialog-centered modal-dialog-scrollable`}>
        <div className="modal-content shadow-lg">
          <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className={`fas ${icone} me-2`} style={{ color: '#00b5cc' }}></i>{titulo}
              </h5>
              {sub && <small style={{ color: '#6c757d' }}>{sub}</small>}
            </div>
            <button type="button" className="btn-close" aria-label="Fechar" onClick={onFechar}></button>
          </div>
          <div className="modal-body">{children}</div>
          <div className="modal-footer bg-light">{rodape}</div>
        </div>
      </div>
    </div>
  );
}

// Provedor da ordem de download das notas (aviso do card)
const ROTULO_PROVEDOR = { nacional: 'Portal Nacional', sao_paulo: 'Prefeitura de SP' };

// Documentos novos de uma ordem: o ADN devolve resultado.novos; São Paulo, contagem por etapa
const novosDe = (o) => {
  const r = o?.resultado;
  if (!r) return undefined;
  if (r.novos != null) return r.novos;
  if (r.contagem) return (r.contagem.recebidas?.novas || 0) + (r.contagem.emitidas?.novas || 0);
  return undefined;
};

function SeloDoc({ situacao }) {
  const [cls, ico, txt] = SITUACAO_DOC[situacao] || SITUACAO_DOC.pendente;
  return <span className={`fz-selo ${cls}`}><i className={`fas ${ico}`}></i>{txt}</span>;
}

// ------------------------------------------------- apresentação (seções e "o que falta")
const NIVEL_PAINEL = {                                      // vermelho → amarelo → azul
  vermelho: { ordem: 0, cls: 'erro', ico: 'fa-circle-exclamation', rotulo: 'Obrigatório' },
  amarelo: { ordem: 1, cls: 'alerta', ico: 'fa-triangle-exclamation', rotulo: 'Recomendado' },
  azul: { ordem: 2, cls: 'neutro', ico: 'fa-circle-info', rotulo: 'Informativo' },
};
// em que seção cada item do painel se resolve (histórico/mês anterior: botão no próprio painel)
const ITEM_SECAO = {
  guias: 1, dctfweb: 1, notas: 2, notas_incompletas: 2, faturamento: 2, trimestre: 2, trimestre_divergente: 2,
  contatos: 3,
};
const cnpjFmt = (v) => {
  const d = String(v || '').replace(/\D/g, '');
  return d.length === 14 ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : String(v || '');
};
// texto curto do problema de um PDF (uma linha, sem repetir a frase longa do servidor)
const problemaDoc = (d) => {
  switch (d.situacao) {
    case 'outra_empresa': {
      const cnpj = d.leitura?.cnpj || (String(d.erro || '').match(/\d{14}/) || [])[0];
      return cnpj ? `PDF de outra empresa (CNPJ ${cnpjFmt(cnpj)})` : 'PDF de outra empresa';
    }
    case 'outra_competencia': return d.competencia_lida ? `PDF da competência ${compBr(d.competencia_lida)}` : (d.erro || 'PDF de outra competência');
    case 'ilegivel': return 'PDF sem texto e sem tributos reconhecidos';
    case 'erro_ia': return d.erro || 'A IA não conseguiu ler o PDF';
    default: return d.erro || '';
  }
};

// seção plana (sem moldura): número da etapa + título h5, subtítulo discreto e selo à direita
function Secao({ n, titulo, sub, selo, ativo, feito, children }) {
  const id = `ffc-secao-${n}`;
  return (
    <section className="ffc-bloco" aria-labelledby={id}>
      <div className="ffc-bloco-head">
        <h5 className="mb-1 ffc-bloco-titulo" id={id}>
          <span className={`ffc-num${ativo ? ' ativo' : ''}${feito ? ' feito' : ''}`} aria-hidden="true">{feito ? <i className="fas fa-check"></i> : n}</span>
          <span className="visually-hidden">Etapa {n}: </span>{titulo}
        </h5>
        {selo}
      </div>
      {sub && <p className="text-muted small mb-3 ffc-bloco-sub">{sub}</p>}
      {children}
    </section>
  );
}

// ------------------------------------------- aba "Conferência dos cálculos" (23/10/2026)
// Mostra a memória de cálculo (contexto.memoria) de cada número do e-mail, com a fórmula por extenso.
const numBr = (v, casas = 2) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas }));
const pctBr = (v, casas = 2) => (v == null ? '—' : `${numBr(v, casas)}%`);
const ORIGEM_FAT = {
  gravado: ['ok', 'fa-database', 'gravado'],
  estimado: ['alerta', 'fa-calculator', 'estimado'],
};

function SeloOrigem({ origem }) {
  if (!origem) return <span className="fz-selo neutro"><i className="fas fa-minus"></i>sem dado</span>;
  const [cls, ico, txt] = ORIGEM_FAT[origem] || ['neutro', 'fa-circle-info', origem];
  return <span className={`fz-selo ${cls}`}><i className={`fas ${ico}`}></i>{txt}</span>;
}

function Formula({ children }) {
  return <div className="ffc-formula fz-num">{children}</div>;
}

function TabelaCarga({ titulo, carga }) {
  if (!carga) return null;
  const itens = carga.itens || [];
  return (
    <div className="mb-3">
      <h6 className="ffc-sub-titulo mb-2">{titulo}</h6>
      {itens.length === 0 ? (
        <div className="small text-muted mb-2">Nenhum imposto gravado neste mês.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm po-table align-middle mb-2">
            <thead><tr><th>Tributo</th><th className="text-end">Valor</th><th>Na carga?</th></tr></thead>
            <tbody>
              {itens.map((i) => (
                <tr key={i.tributo} className={i.entrou ? '' : 'ffc-fora'}>
                  <td>{i.rotulo}</td>
                  <td className="text-end fz-num">{brl(i.valor)}</td>
                  <td className="small">
                    {i.entrou ? <span className="fz-selo ok"><i className="fas fa-check"></i>entra</span>
                      : <span className="fz-selo neutro" title={i.motivo || ''}><i className="fas fa-minus"></i>fora · {i.motivo}</span>}
                  </td>
                </tr>
              ))}
              <tr className="ffc-total"><td>Total na carga</td><td className="text-end fz-num">{brl(carga.total)}</td><td></td></tr>
            </tbody>
          </table>
        </div>
      )}
      <Formula>
        {carga.percentual == null
          ? 'Sem faturamento no mês: a carga não é calculada.'
          : `Impostos ${brl(carga.total)} ÷ faturamento ${brl(carga.faturamento)} = ${pctBr(carga.percentual)} (no e-mail: ${pctBr(carga.percentual, 1)})`}
      </Formula>
    </div>
  );
}

function formulaProjecao(i) {
  if (i.metodo === 'sobre_receita') {
    return `${i.rotulo} ${brl(i.valor_mes)} ÷ faturamento ${brl(i.faturamento_mes)} = ${pctBr(i.aliquota_efetiva, 4)} × média ${brl(i.media_usada)} = ${brl(i.projecao)}`;
  }
  if (i.metodo === 'media_valores') {
    const vals = i.valores_media || [];
    const partes = vals.map((x) => `${x.competencia} ${brl(x.valor)}`).join(' + ');
    return vals.length ? `Média de ${vals.length} mês(es): (${partes}) ÷ ${vals.length} = ${brl(i.projecao)}` : `Média dos meses anteriores = ${brl(i.projecao)}`;
  }
  return `Sem projeção: ${i.motivo_sem || 'sem base'}.`;
}

function ConferenciaCalculos({ memoria, ctx, mostrarConfirmacao, conferido, setConferido }) {
  if (!memoria) {
    return <div className="text-muted small py-3"><i className="fas fa-circle-info me-2"></i>Memória de cálculo indisponível: clique em "Atualizar".</div>;
  }
  const { carga, projecao: proj, trimestre: tri, acumulado: acum } = memoria;
  const vari = memoria.variacao || [];
  const mesesFat = proj?.meses || [];
  const usados = mesesFat.filter((m) => m.valor != null);
  const itensProj = proj?.itens || [];
  const todosProjetados = itensProj.length > 0 && itensProj.every((i) => i.projecao != null);
  const totalProj = todosProjetados ? itensProj.reduce((s, i) => s + Number(i.projecao), 0) : null;
  const somaVar = (campo) => (vari.every((l) => l[campo] == null) ? null : vari.reduce((s, l) => s + Number(l[campo] || 0), 0));
  const totAnt = somaVar('anterior');
  const totAtual = somaVar('atual');
  const totVar = totAnt && totAtual != null ? ((totAtual - totAnt) / totAnt) * 100 : null;
  const divTri = (tri?.divergencias || []).reduce((m, d) => ({ ...m, [d.tributo]: d }), {});
  const nTri = tri?.n_meses || 3;

  return (
    <div className="ffc-conferencia">
      <p className="text-muted small mb-3">
        A base de cada número do e-mail, com a conta por extenso. Confira aqui antes de enviar; qualquer correção
        (imposto, faturamento, mês anterior) é feita na aba "Dados do fechamento" e esta conferência é refeita.
      </p>

      {/* Carga tributária */}
      <section className="ffc-bloco" aria-labelledby="ffc-mc-carga">
        <h5 className="mb-2" id="ffc-mc-carga"><i className="fas fa-percent me-2 ffc-ciano" aria-hidden="true"></i>Carga tributária efetiva</h5>
        <TabelaCarga titulo={`${ctx.mes_rotulo} (mês do fechamento)`} carga={carga?.atual} />
        <TabelaCarga titulo={`${ctx.mes_anterior_rotulo} (comparação)`} carga={carga?.anterior} />
      </section>

      {/* Projeção do próximo mês */}
      <section className="ffc-bloco" aria-labelledby="ffc-mc-proj">
        <h5 className="mb-2" id="ffc-mc-proj"><i className="fas fa-chart-line me-2 ffc-ciano" aria-hidden="true"></i>Projeção de {ctx.mes_proximo_rotulo}</h5>
        <h6 className="ffc-sub-titulo mb-2">Média do faturamento (3 meses anteriores)</h6>
        <div className="table-responsive">
          <table className="table table-sm po-table align-middle mb-2">
            <thead><tr><th>Competência</th><th className="text-end">Faturamento</th><th>Origem</th></tr></thead>
            <tbody>
              {mesesFat.map((m) => (
                <tr key={m.competencia}>
                  <td className="fz-num">{m.competencia}</td>
                  <td className="text-end fz-num">{brl(m.valor)}</td>
                  <td><SeloOrigem origem={m.origem} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Formula>
          {usados.length
            ? `Média = (${usados.map((m) => brl(m.valor)).join(' + ')}) ÷ ${usados.length} = ${brl(proj.media_faturamento)}`
            : 'Sem faturamento nos 3 meses anteriores: os tributos sobre a receita ficam sem projeção.'}
        </Formula>
        {usados.some((m) => m.origem === 'estimado') && (
          <div className="fz-sub mb-2"><i className="fas fa-circle-info me-1"></i>Estimado = soma bruta das NFS-e emitidas no Monitor (mês não gravado no fechamento).</div>
        )}

        <h6 className="ffc-sub-titulo mt-3 mb-2">Tributo a tributo</h6>
        {itensProj.length === 0 ? (
          <div className="small text-muted">Nenhum imposto no mês: não há projeção.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm po-table align-middle mb-2">
              <thead><tr><th>Tributo</th><th>Cálculo</th><th className="text-end">Projeção</th></tr></thead>
              <tbody>
                {itensProj.map((i) => (
                  <tr key={i.tributo} className={i.metodo === 'sem_projecao' ? 'ffc-fora' : ''}>
                    <td className="fw-semibold">{i.rotulo}</td>
                    <td className="small fz-num ffc-calc">
                      <span className="ffc-metodo">{i.metodo === 'sobre_receita' ? 'alíquota efetiva × média do faturamento'
                        : i.metodo === 'media_valores' ? 'média dos valores dos meses anteriores' : 'sem projeção'}</span>
                      {formulaProjecao(i)}
                    </td>
                    <td className="text-end fz-num">{brl(i.projecao)}</td>
                  </tr>
                ))}
                <tr className="ffc-total">
                  <td>Total</td>
                  <td className="small">{todosProjetados ? 'soma das projeções' : 'algum tributo sem projeção: o total sai como "—" no e-mail'}</td>
                  <td className="text-end fz-num">{brl(totalProj)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Variação */}
      <section className="ffc-bloco" aria-labelledby="ffc-mc-var">
        <h5 className="mb-2" id="ffc-mc-var"><i className="fas fa-arrow-right-arrow-left me-2 ffc-ciano" aria-hidden="true"></i>Variação {ctx.mes_anterior_rotulo} → {ctx.mes_rotulo}</h5>
        {vari.length === 0 ? <div className="small text-muted">Nenhum imposto nos dois meses.</div> : (
          <>
            <div className="table-responsive">
              <table className="table table-sm po-table align-middle mb-2">
                <thead><tr><th>Tributo</th><th className="text-end">{ctx.mes_anterior_rotulo}</th><th className="text-end">{ctx.mes_rotulo}</th><th className="text-end">Variação</th></tr></thead>
                <tbody>
                  {vari.map((l) => (
                    <tr key={l.tributo}>
                      <td>{l.rotulo}</td>
                      <td className="text-end fz-num">{brl(l.anterior)}</td>
                      <td className="text-end fz-num">{brl(l.atual)}</td>
                      <td className="text-end fz-num" title={l.variacao == null ? 'sem valor nos dois meses' : `(${brl(l.atual)} − ${brl(l.anterior)}) ÷ ${brl(l.anterior)}`}>{pctBr(l.variacao, 1)}</td>
                    </tr>
                  ))}
                  <tr className="ffc-total">
                    <td>Total</td><td className="text-end fz-num">{brl(totAnt)}</td><td className="text-end fz-num">{brl(totAtual)}</td>
                    <td className="text-end fz-num">{pctBr(totVar, 1)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <Formula>Variação = (valor de {ctx.mes_rotulo} − valor de {ctx.mes_anterior_rotulo}) ÷ valor de {ctx.mes_anterior_rotulo}; sem valor em um dos meses fica "—".</Formula>
          </>
        )}
      </section>

      {/* Apuração trimestral (Presumido) */}
      {tri && (
        <section className="ffc-bloco" aria-labelledby="ffc-mc-tri">
          <h5 className="mb-2" id="ffc-mc-tri"><i className="fas fa-layer-group me-2 ffc-ciano" aria-hidden="true"></i>Apuração trimestral (Lucro Presumido)</h5>
          <div className="table-responsive">
            <table className="table table-sm po-table align-middle mb-2">
              <thead><tr><th>Competência</th><th className="text-end">Faturamento</th><th>Origem</th></tr></thead>
              <tbody>
                {(tri.meses || []).map((m) => (
                  <tr key={m.competencia} className={m.faturamento == null ? 'table-warning' : ''}>
                    <td className="fz-num">{m.competencia}</td>
                    <td className="text-end fz-num">{brl(m.faturamento)}</td>
                    <td><SeloOrigem origem={m.origem} />{m.faturamento == null && <span className="fz-sub ms-2">não entra: só mês gravado</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!tri.completo ? (
            <div className="small text-muted">Faltam meses gravados ({(tri.faltando || []).join(', ')}): o cálculo trimestral só sai com os 3 meses gravados no fechamento.</div>
          ) : (
            <>
              <div className="ffc-passos">
                <Formula>Faturamento do trimestre = {(tri.meses || []).map((m) => brl(m.faturamento)).join(' + ')} = {brl(tri.faturamento)}</Formula>
                <Formula>Limite da presunção de 32% = R$ 416.666,67 × {nTri} = {brl(tri.limite)}</Formula>
                <Formula>Até o limite: {brl(tri.faturamento_ate_limite)} × 32% = {brl(tri.parcela_32)}</Formula>
                <Formula>Excedente: {brl(tri.faturamento_excedente)} × 35,2% = {brl(tri.excedente_352)}</Formula>
                <Formula><b>Base presumida = {brl(tri.parcela_32)} + {brl(tri.excedente_352)} = {brl(tri.base)}</b></Formula>
                <Formula>IRPJ 15%: {brl(tri.base)} × 15% = {brl(tri.irpj_15)}</Formula>
                <Formula>Adicional 10%: ({brl(tri.base)} − R$ 20.000,00 × {nTri} = {brl(tri.adicional_limite)}) = {brl(tri.adicional_base)} × 10% = {brl(tri.adicional_10)}</Formula>
                <Formula><b>IRPJ = {brl(tri.irpj_15)} + {brl(tri.adicional_10)} = {brl(tri.irpj)}</b></Formula>
                <Formula><b>CSLL 9%: {brl(tri.base)} × 9% = {brl(tri.csll)}</b></Formula>
              </div>
              <h6 className="ffc-sub-titulo mt-3 mb-2">Calculado × lido nos DARFs</h6>
              <div className="table-responsive">
                <table className="table table-sm po-table align-middle mb-0">
                  <thead><tr><th>Tributo</th><th className="text-end">Calculado</th><th className="text-end">Lido</th><th className="text-end">Diferença</th></tr></thead>
                  <tbody>
                    {[['IRPJ', tri.irpj, tri.lido_irpj], ['CSLL', tri.csll, tri.lido_csll]].map(([t, calc, lido]) => (
                      <tr key={t} className={divTri[t] ? 'table-danger' : ''}>
                        <td className="fw-semibold">{t}</td>
                        <td className="text-end fz-num">{brl(calc)}</td>
                        <td className="text-end fz-num">{brl(lido)}</td>
                        <td className="text-end fz-num">
                          {divTri[t] ? <><i className="fas fa-triangle-exclamation me-1"></i>{brl(divTri[t].diferenca)}</>
                            : lido == null ? <span className="text-muted">não lido</span>
                              : <span className="fz-selo ok"><i className="fas fa-check"></i>confere</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="fz-sub mt-1">Diferença de até R$ 1,00 é tolerada (arredondamento).</div>
            </>
          )}
        </section>
      )}

      {/* Receita acumulada */}
      {acum && (
        <section className="ffc-bloco" aria-labelledby="ffc-mc-acum">
          <h5 className="mb-2" id="ffc-mc-acum"><i className="fas fa-chart-column me-2 ffc-ciano" aria-hidden="true"></i>Receita acumulada no ano</h5>
          <div className="table-responsive">
            <table className="table table-sm po-table align-middle mb-2">
              <thead><tr><th>Competência</th><th className="text-end">Faturamento</th><th>Origem</th></tr></thead>
              <tbody>
                {(acum.meses || []).map((m) => (
                  <tr key={m.competencia}>
                    <td className="fz-num">{m.competencia}</td>
                    <td className="text-end fz-num">{brl(m.valor)}</td>
                    <td><SeloOrigem origem={m.origem} /></td>
                  </tr>
                ))}
                <tr className="ffc-total"><td>Total</td><td className="text-end fz-num">{brl(acum.total)}</td><td></td></tr>
              </tbody>
            </table>
          </div>
          <Formula>
            {acum.limite
              ? `${brl(acum.total)} ÷ limite ${brl(acum.limite)} = ${pctBr(acum.percentual)} do limite`
              : (acum.regime || 'Regime sem limite cadastrado.')}
          </Formula>
          {acum.limite && acum.regime && <div className="fz-sub">Limite: {acum.regime}</div>}
          {(acum.faltando || []).length > 0 && (
            <div className="fz-sub mt-1"><i className="fas fa-circle-info me-1"></i>Sem faturamento em {acum.faltando.join(', ')}: estes meses não entram na soma.</div>
          )}
          {acum.estimado && (
            <div className="fz-sub mt-1"><i className="fas fa-triangle-exclamation me-1"></i>Há mês estimado pela soma bruta das NFS-e: o e-mail marca o acumulado como estimado.</div>
          )}
        </section>
      )}

      {mostrarConfirmacao && (
        <div className={`ffc-conferido${conferido ? ' ok' : ''}`}>
          <input type="checkbox" className="form-check-input m-0" id="ffc-conferido" checked={conferido}
            onChange={(e) => setConferido(e.target.checked)} />
          <label htmlFor="ffc-conferido" className="mb-0 fw-semibold">Conferi a base de cálculo e os valores</label>
          <span className="fz-sub ms-auto">obrigatório para enviar; desmarca sozinho se os números mudarem</span>
        </div>
      )}
    </div>
  );
}

export default function FiscalFechamentoCliente({ empresaCod, competencia, setErro, setMsg }) {
  const [comp, setComp] = useState(competencia);           // muda no "Registrar mês anterior"
  const [ctx, setCtx] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [ocupado, setOcupado] = useState('');              // ação em andamento (desabilita botões)
  const [download, setDownload] = useState(null);          // download das notas acompanhado aqui: {ordemId, provedor, etapa, progresso, rodada, novos, historico, periodo}
  const [confirmarHistorico, setConfirmarHistorico] = useState(null); // modal do "Buscar histórico": { de, ate, todo }
  const [lendo, setLendo] = useState(null);                // { fase, atual, total, nome }
  const [arrastando, setArrastando] = useState(false);
  const [previa, setPrevia] = useState(null);              // { carregando, dados }
  const [confirmarEnvio, setConfirmarEnvio] = useState(false);
  const [excluir, setExcluir] = useState(null);            // documento
  const [reabrir, setReabrir] = useState(null);            // { motivo }
  const [editando, setEditando] = useState(null);          // { tributo, valor, vencimento, novo }
  const [fatManual, setFatManual] = useState(null);        // texto do faturamento manual (null = fechado)
  const [justificativa, setJustificativa] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [novoEmail, setNovoEmail] = useState({ nome: '', email: '' });
  const [aba, setAba] = useState('dados');                 // dados | calculos (coluna da esquerda)
  const [conferido, setConferido] = useState(false);       // "Conferi a base de cálculo e os valores"
  const [resultadoEnvio, setResultadoEnvio] = useState(null); // { chave, tipo: ok|alerta, texto } do último envio de PDFs
  const [excluirLote, setExcluirLote] = useState(null);    // documentos de outra empresa a remover
  const [verProblemas, setVerProblemas] = useState(true);  // grupo "Com problema" aberto
  const [verLidos, setVerLidos] = useState({ chave: null, aberto: false }); // lista de lidos aberta à mão
  const [previaViva, setPreviaViva] = useState({ carregando: false, dados: null, erro: '' }); // coluna da direita
  const [tentativaPrevia, setTentativaPrevia] = useState(0);
  const pedidoPrevia = useRef(0);                           // só a resposta da última prévia vale
  const arquivoRef = useRef(null);
  const fatRef = useRef(null);
  const justRef = useRef(null);
  const emailRef = useRef(null);
  const montado = useRef(true);
  const pedido = useRef(0);                                 // só a resposta do último GET vale
  const ultimaCompetencia = useRef(null);                   // null = primeira carga (sem espera)
  useEffect(() => { montado.current = true; return () => { montado.current = false; }; }, []);

  // empresa/competência do pai mudou: volta a competência deste card no mesmo render
  // (sem um GET extra da competência antiga) e some com os dados velhos
  const [origem, setOrigem] = useState({ empresaCod, competencia });
  if (origem.empresaCod !== empresaCod || origem.competencia !== competencia) {
    setOrigem({ empresaCod, competencia });
    setComp(competencia);
    setCtx(null);
  }

  const chave = `${empresaCod}|${comp}`;
  const chaveAtual = useRef(chave);
  chaveAtual.current = chave;
  const modoRegistro = comp !== competencia;
  const compValida = Boolean(comp) && comp < mesAtual();
  const params = useMemo(() => ({ empresa: empresaCod, competencia: comp }), [empresaCod, comp]);

  const carregar = useCallback(() => {
    pedido.current += 1;
    const id = pedido.current;
    if (!empresaCod || !comp || comp >= mesAtual()) { setCtx(null); setCarregando(false); return; }
    setCarregando(true);
    const vale = () => montado.current && id === pedido.current;
    axios.get(`${BASE}/`, { params: { empresa: empresaCod, competencia: comp, _: Date.now() }, headers: headers() })
      .then((r) => { if (vale()) setCtx(r.data.contexto); })
      .catch((e) => { if (vale()) { setCtx(null); setErro(e.response?.data?.mensagem || 'Não foi possível carregar o fechamento ao cliente'); } })
      .finally(() => { if (vale()) setCarregando(false); });
  }, [empresaCod, comp, setErro]);
  const carregarRef = useRef(carregar);
  carregarRef.current = carregar;
  const recarregar = () => carregarRef.current();           // ações assíncronas recarregam sempre o card atual

  // uma carga por (empresa, competência); troca de mês no seletor espera ATRASO_MES_MS
  useEffect(() => {
    const trocouMes = ultimaCompetencia.current !== null && ultimaCompetencia.current !== competencia;
    ultimaCompetencia.current = competencia;
    pedido.current += 1;                                    // descarta resposta em voo da chave anterior
    setCtx(null);
    if (!trocouMes) { carregar(); return undefined; }
    setCarregando(true);
    const t = setTimeout(carregar, ATRASO_MES_MS);
    return () => clearTimeout(t);
  }, [carregar, competencia]);

  // textos editáveis acompanham o servidor só quando o valor salvo muda ou o card troca
  // de empresa/mês: ações não relacionadas não apagam o rascunho
  const mensagemServidor = ctx?.mensagem || '';
  const justificativaServidor = ctx?.estado?.justificativa_trimestral || '';
  useEffect(() => { setMensagem(mensagemServidor); }, [mensagemServidor, chave]);
  useEffect(() => { setJustificativa(justificativaServidor); }, [justificativaServidor, chave]);
  // a conferência vale para os números que o operador viu: desmarca ao trocar de empresa/mês e
  // sempre que o contexto recarregado traz outra memória de cálculo (imposto, faturamento, histórico)
  const assinaturaMemoria = useMemo(() => JSON.stringify(ctx?.memoria ?? null), [ctx]);
  useEffect(() => { setConferido(false); }, [assinaturaMemoria, chave]);

  const aplicar = (origemChave) => (r) => {
    if (!montado.current || origemChave !== chaveAtual.current) return;
    if (r.data.contexto) setCtx(r.data.contexto);
    if (r.data.mensagem) setMsg(r.data.mensagem);
  };
  const falhar = (padrao) => (e) => { if (montado.current) setErro(e.response?.data?.mensagem || padrao); };
  const executar = (acao, promessa, padrao) => {
    setOcupado(acao); setErro('');
    return promessa.then(aplicar(chave)).catch(falhar(padrao)).finally(() => { if (montado.current) setOcupado(''); });
  };
  // prévia ao vivo (coluna da direita): refaz o GET previa/ com espera a cada contexto novo;
  // só leitura — enviar continua só pelo modal. Respostas atrasadas são descartadas.
  useEffect(() => {
    pedidoPrevia.current += 1;
    const id = pedidoPrevia.current;
    if (!ctx || modoRegistro || !compValida) { setPreviaViva({ carregando: false, dados: null, erro: '' }); return undefined; }
    setPreviaViva((p) => ({ ...p, carregando: true, erro: '' }));
    const vale = () => montado.current && id === pedidoPrevia.current;
    const t = setTimeout(() => {
      axios.get(`${BASE}/previa/`, { params: { empresa: empresaCod, competencia: comp, _: Date.now() }, headers: headers() })
        .then((r) => { if (vale()) setPreviaViva({ carregando: false, dados: r.data.previa, erro: '' }); })
        .catch((e) => { if (vale()) setPreviaViva({ carregando: false, dados: null, erro: e.response?.data?.mensagem || 'Não foi possível montar a prévia' }); });
    }, ATRASO_PREVIA_MS);
    return () => clearTimeout(t);
  }, [ctx, modoRegistro, compValida, empresaCod, comp, tentativaPrevia]);

  const salvar = (campos, chave = 'salvar') => executar(chave, axios.put(`${BASE}/`, { ...params, ...campos }, { headers: headers() }), 'Não foi possível salvar');

  // Rascunho da "Mensagem do assessor" pela IA (mesma chamada do fechamento contábil): só
  // preenche o texto local — nada é salvo até o operador revisar e clicar em "Salvar mensagem".
  const gerarMensagemIa = () => {
    setOcupado('mensagem-ia'); setErro('');
    const origemChave = chave;
    axios.post(`${BASE}/mensagem-ia/`, params, { headers: headers(), timeout: 420000 })
      .then((r) => {
        if (!montado.current || origemChave !== chaveAtual.current) return;
        setMensagem(r.data.mensagem_ia || '');
        setMsg('Rascunho gerado. Revise e salve antes de enviar.');
      })
      .catch(falhar('Não foi possível gerar o rascunho com IA'))
      .finally(() => { if (montado.current) setOcupado(''); });
  };

  // ------------------------------------------------------------------ PDFs
  const subir = async (lista) => {
    const todos = [...(lista || [])];
    if (!todos.length) return;
    const recusados = [];
    const pdfs = todos.filter((f) => {
      if (!/\.pdf$/i.test(f.name) && f.type !== 'application/pdf') { recusados.push(`${f.name}: não é PDF`); return false; }
      if (f.size > MAX_MB * 1024 * 1024) { recusados.push(`${f.name}: acima de ${MAX_MB} MB`); return false; }
      return true;
    });
    const vagas = MAX_DOCS - (ctx?.documentos?.length || 0);
    if (pdfs.length > vagas) {
      pdfs.splice(Math.max(vagas, 0)).forEach((f) => recusados.push(`${f.name}: limite de ${MAX_DOCS} PDFs por fechamento`));
    }
    setErro('');
    setResultadoEnvio(null);
    const origemChave = chave;
    let lidos = 0;
    let comProblema = 0;
    let naoProcessados = 0;
    const falhas = [...recusados];
    // um PDF por vez: guarda no bucket e lê pela IA, com progresso
    for (let i = 0; i < pdfs.length; i += 1) {
      if (!montado.current) return;
      const f = pdfs[i];
      setLendo({ atual: i + 1, total: pdfs.length, nome: f.name });
      const fd = new FormData();
      fd.append('empresa', empresaCod); fd.append('competencia', comp); fd.append('arquivos', f);
      let ids = [];
      try {
        const r = await axios.post(`${BASE}/documentos/`, fd, { headers: headers() });
        ids = r.data.ids || [];
      } catch (e) {
        // 400 com ids = parte foi guardada no bucket: lê o que entrou e mostra o erro
        if (e.response?.status === 400 && Array.isArray(e.response?.data?.ids)) ids = e.response.data.ids;
        falhas.push(e.response?.data?.mensagem || `${f.name}: não foi possível guardar`);
        if (!ids.length) naoProcessados += 1;
      }
      for (let j = 0; j < ids.length; j += 1) {
        if (!montado.current) return;
        try {
          const r = await axios.post(`${BASE}/documentos/${ids[j]}/ler/`, {}, { headers: headers() });
          // o /ler/ devolve a situação: só 'ok' conta como lido; o resto (outra empresa etc.) é problema
          if (!r.data?.situacao || r.data.situacao === 'ok') lidos += 1; else comProblema += 1;
        } catch (e) {
          naoProcessados += 1;
          falhas.push(e.response?.data?.mensagem || `${f.name}: não foi possível ler`);
        }
      }
    }
    if (!montado.current) return;
    setLendo(null);
    if (arquivoRef.current) arquivoRef.current.value = '';
    if (falhas.length) setErro(falhas.join(' · '));
    // resultado real do envio, dentro do card: verde só quando tudo foi lido sem problema
    if (pdfs.length) {
      const partes = [`${lidos} lido(s)`, `${comProblema} com problema`];
      if (naoProcessados) partes.push(`${naoProcessados} não processado(s)`);
      if (recusados.length) partes.push(`${recusados.length} recusado(s)`);
      const tudoOk = !comProblema && !naoProcessados && !recusados.length;
      setResultadoEnvio({ chave: origemChave, tipo: tudoOk ? 'ok' : 'alerta',
        texto: `${pdfs.length} PDF(s) enviado(s): ${partes.join(', ')}` });
    }
    recarregar();
  };

  const lerDeNovo = (doc) => {
    setOcupado(`ler-${doc.id}`); setErro('');
    axios.post(`${BASE}/documentos/${doc.id}/ler/`, {}, { headers: headers() })
      .then(() => recarregar())
      .catch(falhar('Não foi possível ler o PDF'))
      .finally(() => { if (montado.current) setOcupado(''); });
  };

  const mover = (doc) => {
    setOcupado(`mover-${doc.id}`); setErro('');
    axios.post(`${BASE}/documentos/${doc.id}/mover/`, { competencia: doc.competencia_lida }, { headers: headers() })
      .then(() => { if (montado.current) { setMsg(`${doc.nome} movido para ${compBr(doc.competencia_lida)}`); recarregar(); } })
      .catch(falhar('Não foi possível mover o PDF'))
      .finally(() => { if (montado.current) setOcupado(''); });
  };

  // remove em lote os PDFs de outra empresa: o mesmo DELETE de um por um, com confirmação
  const confirmarExclusaoLote = async () => {
    const lista = excluirLote || [];
    const origemChave = chave;
    setOcupado('excluir-lote'); setErro('');
    let removidos = 0;
    const falhas = [];
    for (let i = 0; i < lista.length; i += 1) {
      if (!montado.current) return;
      try {
        await axios.delete(`${BASE}/documentos/${lista[i].id}/`, { headers: headers() });
        removidos += 1;
      } catch (e) {
        falhas.push(e.response?.data?.mensagem || `${lista[i].nome}: não foi possível excluir`);
      }
    }
    if (!montado.current) return;
    setOcupado(''); setExcluirLote(null);
    if (falhas.length) setErro(falhas.join(' · '));
    if (removidos && origemChave === chaveAtual.current) setMsg(`${removidos} PDF(s) de outra empresa removido(s)`);
    recarregar();
  };

  const confirmarExclusao = () => {
    const doc = excluir;
    setOcupado(`excluir-${doc.id}`); setErro('');
    axios.delete(`${BASE}/documentos/${doc.id}/`, { headers: headers() })
      .then(() => { if (montado.current) { setExcluir(null); setMsg(`${doc.nome} excluído`); recarregar(); } })
      .catch(falhar('Não foi possível excluir o PDF'))
      .finally(() => { if (montado.current) setOcupado(''); });
  };

  // --------------------------------------------------------------- impostos
  const salvarImposto = () => {
    const e = editando;
    if (!e.tributo || !String(e.valor).trim()) { setErro('Informe o tributo e o valor'); return; }
    executar('imposto', axios.put(`${BASE}/impostos/`, { ...params, tributo: e.tributo, valor: e.valor, vencimento: e.vencimento || null },
      { headers: headers() }), 'Não foi possível salvar o imposto')
      .then(() => { if (montado.current) setEditando(null); });
  };
  const removerImposto = (tributo) => executar(`rm-${tributo}`, axios.delete(`${BASE}/impostos/`,
    { params: { ...params, tributo }, headers: headers() }), 'Não foi possível remover o imposto');

  // ------------------------------------------------------------------ notas
  const notas = (acao, periodo) => (acao === 'baixar' || acao === 'historico' ? baixarNotas(acao, periodo) : executar(`notas-${acao}`,
    axios.post(`${BASE}/notas/`, { ...params, acao }, { headers: headers() }), 'Não foi possível carregar as notas'));

  // Carregar notas: cria (ou retoma) a ordem de download do ADN — e, para empresa de São Paulo, a da
  // Prefeitura de SP do mês — e processa cada uma em rodadas de ~4 min pelo endpoint do Monitor, mostrando
  // o andamento no card; no fim relê as notas da competência.
  // acao: 'baixar' (incremental, do último NSU) | 'historico' (sem retroativas; com periodo {inicio, fim}
  // só os meses escolhidos, sem periodo todas as notas desde o NSU 0).
  // Processa uma ordem do Monitor em rodadas de ~4 min até terminar, atualizando o aviso do card.
  // Devolve {o: ordem final} ou {erro: texto}.
  const acompanharOrdem = async (ordem, indice, total, historico, periodo) => {
    const ordemId = ordem.id;
    const etapa = total > 1 ? `${indice + 1}/${total}` : '';
    setDownload({ ordemId, provedor: ordem.provedor, etapa,
      progresso: historico ? 'Iniciando a busca do histórico…' : 'Iniciando o download…',
      rodada: 1, novos: 0, historico, periodo: historico ? periodo : null });
    let acabou = false;
    const timer = setInterval(() => {
      axios.get(`${API}/api/fiscal/monitor/ordens/${ordemId}/`, { params: { _: Date.now() }, headers: headers() })
        .then((r) => {
          const o = r.data.ordem;
          if (!montado.current || acabou || !o) return;
          setDownload((d) => (d && d.ordemId === ordemId
            ? { ...d, progresso: o.progresso || d.progresso, novos: novosDe(o) ?? d.novos } : d));
        })
        .catch(() => {});
    }, 3000);
    const fim = (res) => { acabou = true; clearInterval(timer); return res; };
    let rodada = 1;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let o;
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await axios.post(`${API}/api/fiscal/monitor/ordens/${ordemId}/processar/`, {}, { headers: headers(), timeout: 600000 });
        o = r.data.ordem;
      } catch (e) {
        // ordem já em processamento por outro (job ou outra tela): só acompanha, sem reprocessar
        // (reprocessar de novo daria 409 em laço). Sem andamento por ~10 min = provável órfã.
        if (e.response?.status === 409) {
          let paradas = 0;
          let visto = '';
          for (;;) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((ok) => setTimeout(ok, 5000));
            if (!montado.current) return fim({ o: null });
            let atual;
            try {
              // eslint-disable-next-line no-await-in-loop
              const r = await axios.get(`${API}/api/fiscal/monitor/ordens/${ordemId}/`, { params: { _: Date.now() }, headers: headers() });
              atual = r.data.ordem;
            } catch (e2) { return fim({ erro: 'Não foi possível acompanhar o download das notas' }); }
            if (!atual || atual.status === 'concluida' || atual.status === 'erro') { o = atual; break; }
            if (atual.status === 'pendente') { o = null; break; }
            const marca = `${atual.progresso || ''}|${atual.resultado?.ultimo_nsu ?? ''}|${atual.resultado?.requisicoes ?? ''}`;
            paradas = marca === visto ? paradas + 1 : 0;
            visto = marca;
            if (paradas >= 120) {
              return fim({ erro: 'O download parou de andar há uns 10 minutos (provável queda do servidor no meio). Clique de novo em alguns instantes: o Saber libera a ordem presa e continua.' });
            }
          }
          if (o === null) { continue; }
        } else {
          return fim({ erro: e.response?.data?.mensagem || 'Não foi possível processar o download das notas' });
        }
      }
      if (!montado.current) return fim({ o });
      if (o?.status === 'pendente' && rodada < 500) {
        rodada += 1;
        const rodadaAtual = rodada;
        const novosAgora = novosDe(o);
        setDownload((d) => (d ? { ...d, rodada: rodadaAtual, novos: novosAgora ?? d.novos } : d));
        continue;
      }
      return fim({ o });
    }
  };

  const baixarNotas = async (acao = 'baixar', periodo = null) => {
    const origem = chave;
    const historico = acao === 'historico';
    setErro(''); setOcupado(`notas-${acao}`);
    let ordens;
    try {
      const corpo = historico && periodo ? { ...params, acao, periodo } : { ...params, acao };
      const r = await axios.post(`${BASE}/notas/`, corpo, { headers: headers() });
      // empresa de São Paulo: vêm duas ordens (Portal Nacional e Prefeitura de SP), processadas uma por vez
      ordens = r.data.ordens?.length ? r.data.ordens : [{ id: r.data.ordem_id, provedor: 'nacional' }];
    } catch (e) {
      falhar(historico ? 'Não foi possível iniciar a busca do histórico' : 'Não foi possível iniciar o download das notas')(e);
      if (montado.current) setOcupado('');
      return;
    }
    if (!montado.current || origem !== chaveAtual.current) return;
    const resultados = [];
    for (let i = 0; i < ordens.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await acompanharOrdem(ordens[i], i, ordens.length, historico, periodo);
      if (!montado.current) return;
      resultados.push({ ...res, provedor: ordens[i].provedor });
    }
    setDownload(null);
    setOcupado('');
    const erros = resultados.filter((x) => x.erro || x.o?.status === 'erro')
      .map((x) => `${ROTULO_PROVEDOR[x.provedor] || x.provedor}: ${x.erro || x.o?.mensagem}`);
    const ok = resultados.filter((x) => !x.erro && x.o?.status !== 'erro');
    if (erros.length) setErro(`Download das notas — ${erros.join(' · ')}`);
    if (ok.length) {
      const partes = ok.map((x) => `${ordens.length > 1 ? `${ROTULO_PROVEDOR[x.provedor] || x.provedor}: ` : ''}${novosDe(x.o) ?? 0} documento(s) novo(s)`
        + (historico ? `, ${x.o?.resultado?.existentes ?? 0} já existente(s)` : ''));
      setMsg(historico
        ? `${periodo ? `Histórico de ${periodoBr(periodo)}` : 'Histórico completo'} carregado: ${partes.join('; ')}.`
        : `Notas carregadas: ${partes.join('; ')}.`);
    }
    if (origem === chaveAtual.current) notas('atualizar');
  };

  // -------------------------------------------------------------- destinatários
  const salvarDestinatarios = (lista) => salvar({ destinatarios: lista }, 'destinatarios');
  const alternarDestinatario = (email) => salvarDestinatarios((ctx.destinatarios || []).map((d) => (
    d.email === email ? { ...d, marcado: !(d.marcado ?? true) } : d)));
  const incluirEmail = () => {
    const email = novoEmail.email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setErro('E-mail inválido'); return; }
    if ((ctx.destinatarios || []).some((d) => d.email === email)) { setErro('Este e-mail já está na lista'); return; }
    salvarDestinatarios([...(ctx.destinatarios || []), { nome: novoEmail.nome.trim(), email, marcado: true }])
      .then(() => { if (montado.current) setNovoEmail({ nome: '', email: '' }); });
  };
  const removerEmail = (email) => salvarDestinatarios((ctx.destinatarios || []).filter((d) => d.email !== email));

  // ------------------------------------------------------------- status/envio
  const registrar = () => executar('registrar', axios.post(`${BASE}/registrar/`, params, { headers: headers() }),
    'Não foi possível registrar o mês');

  const abrirPrevia = () => {
    setErro(''); setConfirmarEnvio(false);
    setPrevia({ carregando: true, dados: null });
    axios.get(`${BASE}/previa/`, { params: { ...params, _: Date.now() }, headers: headers() })
      .then((r) => { if (montado.current) setPrevia({ carregando: false, dados: r.data.previa }); })
      .catch((e) => { if (montado.current) { setPrevia(null); setErro(e.response?.data?.mensagem || 'Não foi possível montar a prévia'); } });
  };

  const enviar = () => {
    setOcupado('enviar'); setErro('');
    axios.post(`${BASE}/enviar/`, { ...params, confirmar: true, calculos_conferidos: conferido }, { headers: headers() })
      .then((r) => {
        if (!montado.current) return;
        setPrevia(null); setConfirmarEnvio(false);
        if (r.data.contexto) { aplicar(chave)(r); return; }
        // saiu, mas não foi marcado como enviado: recarrega e avisa (não é sucesso completo)
        recarregar();
        setErro(r.data.mensagem || 'O e-mail saiu, mas o fechamento não foi marcado como enviado: confira antes de enviar de novo');
      })
      .catch((e) => { if (montado.current) { setConfirmarEnvio(false); setErro(e.response?.data?.mensagem || 'Não foi possível enviar o fechamento'); setPrevia(null); } })
      .finally(() => { if (montado.current) setOcupado(''); });
  };

  const confirmarReabertura = () => {
    const motivo = (reabrir?.motivo || '').trim();
    if (motivo.length < 5) { setErro('Informe o motivo da reabertura'); return; }
    executar('reabrir', axios.post(`${BASE}/reabrir/`, { ...params, motivo }, { headers: headers() }), 'Não foi possível reabrir')
      .then(() => { if (montado.current) setReabrir(null); });
  };

  // ------------------------------------------------------------------ derivados
  const estado = ctx?.estado || {};
  const travado = estado.status === 'enviado';
  const podeEnviar = !modoRegistro && !travado;            // só aí a conferência dos cálculos é exigida
  const impostos = useMemo(() => TRIBUTOS.filter((t) => ctx?.impostos?.[t]).map((t) => ({ tributo: t, ...ctx.impostos[t] })), [ctx]);
  const nomeDoc = useMemo(() => Object.fromEntries((ctx?.documentos || []).map((d) => [d.id, d.nome])), [ctx]);
  const conferenciaDctf = useMemo(() => {
    const doc = (ctx?.documentos || []).find((d) => d.tipo === 'dctfweb' && d.situacao === 'ok' && d.leitura?.conferencia);
    if (!doc) return [];
    return DCTF_CONFERE.filter((t) => doc.leitura.conferencia[t] != null).map((t) => {
      const declarado = Number(doc.leitura.conferencia[t]);
      const darf = ctx.impostos?.[t]?.valor;
      return { tributo: t, declarado, darf, diverge: darf == null || Math.abs(Number(darf) - declarado) > 0.01 };
    }).filter((x) => x.diverge);
  }, [ctx]);
  const painel = useMemo(() => (ctx?.painel || [])
    .filter((p) => !(modoRegistro && ['contatos', 'mes_anterior'].includes(p.item)))
    .map((p, i) => ({ ...p, _i: i }))
    .sort((a, b) => ((NIVEL_PAINEL[a.cor]?.ordem ?? 3) - (NIVEL_PAINEL[b.cor]?.ordem ?? 3)) || a._i - b._i), [ctx, modoRegistro]);
  const tributosLivres = TRIBUTOS.filter((t) => !ctx?.impostos?.[t]);

  // PDFs: lidos, aguardando e com problema (estes vão primeiro, num grupo próprio)
  const documentos = ctx?.documentos || [];
  const docsOk = documentos.filter((d) => d.situacao === 'ok');
  const docsPendentes = documentos.filter((d) => !d.situacao || d.situacao === 'pendente');
  const docsProblema = documentos.filter((d) => d.situacao && !['ok', 'pendente'].includes(d.situacao));
  const docsOutraEmpresa = docsProblema.filter((d) => d.situacao === 'outra_empresa');
  const docsDemais = [...docsPendentes, ...docsOk];

  // progresso "X de Y itens prontos": universo = itens que o painel pode cobrar neste mês
  const universo = new Set(['notas', 'guias', 'dctfweb', 'faturamento', 'historico']);
  if (!modoRegistro) { universo.add('mes_anterior'); universo.add('contatos'); }
  if (ctx?.trimestre) universo.add('trimestre');
  if ((ctx?.trimestre?.divergencias || []).length) universo.add('trimestre_divergente');
  const pendentes = new Set(painel.map((p) => (p.item === 'notas_incompletas' ? 'notas' : p.item)));
  pendentes.forEach((i) => universo.add(i));
  const totalItens = universo.size;
  const itensProntos = totalItens - pendentes.size;

  // situação de cada seção: vermelho (obrigatório pendente), amarelo (revisar) ou feito
  const secoes = modoRegistro ? [1, 2] : [1, 2, 3];
  const marcados = (ctx?.destinatarios || []).filter((d) => d.marcado ?? true).length;
  const nivelSecao = (n) => {
    if (travado) return 'feito';
    const cores = painel.filter((p) => ITEM_SECAO[p.item] === n).map((p) => p.cor);
    if (cores.includes('vermelho')) return 'vermelho';
    if (n === 1 && (!documentos.length || docsPendentes.length)) return 'amarelo';
    if (cores.includes('amarelo') || (n === 1 && docsProblema.length) || (n === 3 && !marcados)) return 'amarelo';
    return 'feito';
  };
  const niveis = Object.fromEntries(secoes.map((n) => [n, nivelSecao(n)]));
  const primeiroIncompleto = secoes.find((n) => niveis[n] !== 'feito');
  const seloSecao = (n) => {
    const nv = niveis[n];
    if (nv === 'vermelho') return <span className="fz-selo erro"><i className="fas fa-circle-exclamation"></i>Obrigatório pendente</span>;
    if (nv === 'amarelo') return <span className="fz-selo alerta"><i className="fas fa-triangle-exclamation"></i>Revisar</span>;
    return <span className="fz-selo ok"><i className="fas fa-circle-check"></i>Pronto</span>;
  };
  const propsSecao = (n) => ({ n, ativo: n === primeiroIncompleto, feito: niveis[n] === 'feito', selo: seloSecao(n) });

  // ---------------------------------------------------------- painel "o que falta"
  const focar = (ref) => setTimeout(() => { ref.current?.focus(); ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 80);
  const irRegistro = (alvo) => { setCtx(null); setEditando(null); setFatManual(null); setComp(alvo); };
  const acaoPainel = (p) => {
    switch (p.acao) {
      case 'carregar': notas('baixar'); break;
      case 'historico': setConfirmarHistorico({ de: comp, ate: comp, todo: false }); break;
      case 'atualizar': notas('atualizar'); break;
      case 'subir': arquivoRef.current?.click(); break;
      case 'digitar':
        if (p.item === 'contatos') focar(emailRef); else { setFatManual(valorTexto(ctx?.faturamento)); focar(fatRef); }
        break;
      case 'justificar': focar(justRef); break;
      default: break;
    }
  };

  if (!empresaCod || !competencia) return null;

  const desab = Boolean(ocupado) || Boolean(lendo) || travado;

  const botaoPainel = (p) => {
    if (p.acao === 'certificado') return <Link to="/Fiscal/Certificados" className="btn btn-sm btn-outline-primary"><i className="fas fa-certificate me-1"></i>Certificados</Link>;
    if (p.acao === 'habilitar') return <Link to="/Fiscal/Monitor" className="btn btn-sm btn-outline-primary"><i className="fas fa-toggle-on me-1"></i>Habilitar no Monitor</Link>;
    if (p.acao === 'carregar' || p.acao === 'atualizar') {
      const chaveNotas = p.acao === 'carregar' ? 'notas-baixar' : 'notas-atualizar';
      return (
        <>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => acaoPainel(p)} disabled={desab}>
            <i className={`fas ${ocupado === chaveNotas ? 'fa-spinner fa-spin' : p.acao === 'carregar' ? 'fa-cloud-arrow-down' : 'fa-rotate'} me-1`}></i>
            {p.acao === 'carregar' ? 'Carregar notas' : 'Atualizar notas'}
          </button>
          {p.acao2 === 'historico' && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => acaoPainel({ ...p, acao: 'historico' })} disabled={desab}>
              <i className={`fas ${ocupado === 'notas-historico' ? 'fa-spinner fa-spin' : 'fa-clock-rotate-left'} me-1`}></i>
              Buscar histórico
            </button>
          )}
        </>
      );
    }
    if (p.acao === 'subir') {
      return (
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => acaoPainel(p)} disabled={desab}>
          <i className="fas fa-file-arrow-up me-1"></i>{p.item === 'dctfweb' ? 'Subir DCTFWeb' : 'Subir PDFs'}
        </button>
      );
    }
    if (p.acao === 'digitar') {
      return (
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => acaoPainel(p)} disabled={desab}>
          <i className="fas fa-keyboard me-1"></i>{p.item === 'contatos' ? 'Informar e-mail' : 'Informar faturamento'}
        </button>
      );
    }
    if (p.acao === 'justificar') {
      return (
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => acaoPainel(p)} disabled={desab}>
          <i className="fas fa-pen me-1"></i>Justificar
        </button>
      );
    }
    if (p.acao === 'registrar' && p.item === 'mes_anterior') {
      return (
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => irRegistro(compAnterior(ctx.ano, ctx.mes))} disabled={Boolean(lendo)}>
          <i className="fas fa-box-archive me-1"></i>Registrar {ctx.mes_anterior_rotulo}
        </button>
      );
    }
    if (p.acao === 'registrar') {
      return (p.meses || []).map(([a, m]) => {
        const alvo = compDe(a, m);
        return alvo === comp ? null : (
          <button key={alvo} type="button" className="btn btn-sm btn-outline-primary" onClick={() => irRegistro(alvo)} disabled={Boolean(lendo)}>
            <i className="fas fa-box-archive me-1"></i>Registrar {compBr(alvo)}
          </button>
        );
      });
    }
    return null;
  };

  const tabelaDocs = (lista) => (
    <div className="table-responsive">
      <table className="table table-sm po-table align-middle mb-0">
        <thead><tr><th>Arquivo</th><th>Tipo</th><th>Situação</th><th>Tributos lidos</th>{!travado && <th className="text-end">Ações</th>}</tr></thead>
        <tbody>
          {lista.map((d) => {
            const trib = Object.entries(d.leitura?.tributos || {});
            const avisos = d.leitura?.avisos || [];
            const problema = d.situacao && !['ok', 'pendente'].includes(d.situacao) ? problemaDoc(d) : '';
            return (
              <tr key={d.id}>
                <td>
                  <div className="small fw-semibold text-truncate ffc-nome" title={d.nome}>{d.nome}</div>
                  {d.competencia_lida && <div className="fz-sub fz-mono">competência lida {compBr(d.competencia_lida)}</div>}
                </td>
                <td className="small">{TIPO_DOC[d.tipo] || '—'}</td>
                <td>
                  <SeloDoc situacao={d.situacao} />
                  {problema && <div className="fz-sub mt-1 ffc-problema" title={d.erro || problema}>{problema}</div>}
                </td>
                <td className="small">
                  {trib.length === 0 && d.leitura?.receita_bruta == null && <span className="text-muted">—</span>}
                  {trib.map(([t, i]) => <div key={t} className="fz-num">{ROTULO_TRIBUTO[t] || t}: {brl(i.valor)}</div>)}
                  {d.leitura?.receita_bruta != null && <div className="fz-num">Receita bruta: {brl(d.leitura.receita_bruta)}</div>}
                  {avisos.map((a) => <div key={a} className="fz-sub text-warning-emphasis"><i className="fas fa-triangle-exclamation me-1"></i>{a}</div>)}
                </td>
                {!travado && (
                  <td className="text-end">
                    <div className="d-inline-flex gap-1 flex-wrap justify-content-end">
                      {d.situacao === 'outra_competencia' && d.competencia_lida && d.competencia_lida < mesAtual() && (
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => mover(d)} disabled={desab}>
                          <i className={`fas ${ocupado === `mover-${d.id}` ? 'fa-spinner fa-spin' : 'fa-right-to-bracket'} me-1`}></i>Mover para {compBr(d.competencia_lida)}
                        </button>
                      )}
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => lerDeNovo(d)} disabled={desab}>
                        <i className={`fas ${ocupado === `ler-${d.id}` ? 'fa-spinner fa-spin' : 'fa-robot'} me-1`}></i>Ler de novo
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setExcluir(d)} disabled={desab}>
                        <i className="fas fa-trash me-1"></i>Excluir
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const resumo1 = documentos.length
    ? `${documentos.length} PDF(s) · ${docsOk.length} lido(s)${docsProblema.length ? ` · ${docsProblema.length} com problema` : ''}${docsPendentes.length ? ` · ${docsPendentes.length} aguardando` : ''}`
    : 'Nenhum PDF ainda: suba as guias e recibos do mês';
  const resumo3 = travado ? 'E-mail enviado a estes contatos.' : `${marcados} destinatário(s) marcado(s) para receber o e-mail.`;
  const verResultado = resultadoEnvio && resultadoEnvio.chave === chave;
  const lidosAbertos = verLidos.chave === chave ? verLidos.aberto : docsDemais.length <= LIDOS_VISIVEIS;
  const pv = previaViva.dados;

  // cabeçalho do card de dados: empresa/mês, progresso, situação e "Atualizar"
  const cabecalho = (
    <div className="card-header bg-light ffc-card-head">
      <div className="ffc-head-txt">
        <h5 className="mb-0"><i className="fas fa-envelope-circle-check me-2 ffc-ciano" aria-hidden="true"></i>Fechamento ao cliente</h5>
        <div className="fz-sub text-truncate" title={ctx?.empresa?.cliente || ''}>
          {ctx ? `${ctx.empresa?.cliente || ''} · ${ctx.mes_rotulo}` : compBr(comp)}
        </div>
      </div>
      <div className="d-flex gap-2 flex-wrap align-items-center ms-auto">
        {ctx && !travado && (
          <div className="ffc-progresso" title="Itens do &quot;O que falta&quot; resolvidos">
            <span className="small fw-semibold fz-num">{itensProntos} de {totalItens} itens prontos</span>
            <div className="progress" role="progressbar" aria-label="Itens prontos" aria-valuemin={0} aria-valuemax={totalItens} aria-valuenow={itensProntos}>
              <div className="progress-bar" style={{ width: `${totalItens ? Math.round((itensProntos / totalItens) * 100) : 0}%` }}></div>
            </div>
          </div>
        )}
        {modoRegistro && <span className="fz-selo alerta"><i className="fas fa-box-archive"></i>Modo registro · {compBr(comp)}</span>}
        {estado.status === 'enviado' && (
          <span className="fz-selo ok"><i className="fas fa-paper-plane"></i>
            Enviado em {dataHoraBr(estado.enviado_em)}{estado.enviado_por_nome ? ` por ${estado.enviado_por_nome}` : ''}
          </span>
        )}
        {estado.status === 'registrado' && <span className="fz-selo neutro"><i className="fas fa-box-archive"></i>Registrado (sem envio)</span>}
        {estado.status === 'rascunho' && <span className="fz-selo neutro"><i className="fas fa-pen"></i>Rascunho</span>}
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={recarregar} disabled={carregando || !compValida}>
          <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-1`}></i>Atualizar
        </button>
      </div>
    </div>
  );

  // botões finais (coluna da direita): o envio só acontece por dentro do modal de prévia
  const botaoRegistrar = !travado && estado.status !== 'registrado' && (
    <button type="button" className={`btn ${modoRegistro ? 'btn-primary' : 'btn-outline-primary'}`} onClick={registrar} disabled={desab}>
      <i className={`fas ${ocupado === 'registrar' ? 'fa-spinner fa-spin' : 'fa-box-archive'} me-2`}></i>Registrar sem enviar
    </button>
  );
  const botaoReabrir = travado && (
    <button type="button" className="btn btn-outline-secondary" onClick={() => setReabrir({ motivo: '' })} disabled={Boolean(ocupado) || Boolean(lendo)}>
      <i className="fas fa-lock-open me-2"></i>Reabrir
    </button>
  );

  return (
    <section className="ffc mb-3 po-enter">
      {!ctx && (
        <div className="card shadow-sm ffc-card">
          {cabecalho}
          <div className="card-body">
            {!compValida ? (
              <div className="text-muted small"><i className="fas fa-circle-info me-2"></i>O fechamento ao cliente é de um mês já encerrado: escolha uma competência anterior ao mês atual.</div>
            ) : (
              <div className="text-muted small py-3 text-center"><i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-circle-info'} me-2`}></i>
                {carregando ? 'Carregando o fechamento ao cliente (a leitura das notas pode levar até 2 minutos)…' : 'Não foi possível carregar.'}
              </div>
            )}
          </div>
        </div>
      )}

      {ctx && (
        <div className="row g-4">
          {/* ------------------------------------------------ coluna da esquerda: dados */}
          <div className="col-lg-7">
            <div className="card shadow-sm ffc-card">
              {cabecalho}
              <div className="card-body ffc-corpo">
                <ul className="nav nav-tabs ffc-abas mb-3" role="tablist" aria-label="Fechamento ao cliente">
                  <li className="nav-item" role="presentation">
                    <button type="button" role="tab" id="ffc-aba-dados" aria-selected={aba === 'dados'} aria-controls="ffc-painel-aba"
                      className={`nav-link${aba === 'dados' ? ' active' : ''}`} onClick={() => setAba('dados')}>
                      <i className="fas fa-file-invoice-dollar me-2" aria-hidden="true"></i>Dados do fechamento
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button type="button" role="tab" id="ffc-aba-calculos" aria-selected={aba === 'calculos'} aria-controls="ffc-painel-aba"
                      className={`nav-link${aba === 'calculos' ? ' active' : ''}`} onClick={() => setAba('calculos')}>
                      <i className="fas fa-calculator me-2" aria-hidden="true"></i>Conferência dos cálculos
                      {podeEnviar && (conferido
                        ? <span className="fz-selo ok ms-2"><i className="fas fa-check"></i>conferido</span>
                        : <span className="fz-selo alerta ms-2"><i className="fas fa-hourglass-half"></i>a conferir</span>)}
                    </button>
                  </li>
                </ul>
                <div id="ffc-painel-aba" role="tabpanel" aria-labelledby={aba === 'dados' ? 'ffc-aba-dados' : 'ffc-aba-calculos'}>
                  {aba === 'calculos' ? (
                    <ConferenciaCalculos memoria={ctx.memoria} ctx={ctx} mostrarConfirmacao={podeEnviar}
                      conferido={conferido} setConferido={setConferido} />
                  ) : (
                    <>
                      {travado && (
                        <div className="alert alert-light border py-2 small"><i className="fas fa-lock me-2"></i>
                          Fechamento enviado ao cliente: a tela está travada. Para alterar, use "Reabrir" (o motivo fica no histórico).
                        </div>
                      )}
                      {estado.reaberto_motivo && (
                        <div className="fz-sub mb-3" style={{ whiteSpace: 'pre-line' }}><i className="fas fa-clock-rotate-left me-1"></i>Reaberturas: {estado.reaberto_motivo}</div>
                      )}

                      {download && (
                        <div className="alert alert-info d-flex align-items-center gap-2 py-2 small" role="status">
                          <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                          <div>
                            <strong>
                              {!download.historico && `Baixando as notas — ${ROTULO_PROVEDOR[download.provedor] || 'Portal Nacional'}`}
                              {download.historico && (download.periodo ? `Buscando o histórico de ${periodoBr(download.periodo)} no ADN` : 'Buscando o histórico completo no ADN')}
                              {download.etapa ? ` (${download.etapa})` : ''}
                            </strong> — {download.progresso}
                            {download.rodada > 1 ? ` · rodada ${download.rodada}` : ''}
                            {download.novos ? ` · ${download.novos} documento(s) novo(s) até agora` : ''}
                            <div className="text-muted">
                              {download.historico ? 'Pode levar de minutos a horas, conforme o volume' : 'Pode levar alguns minutos'}; não feche a página. Ao terminar, o faturamento é atualizado sozinho.
                            </div>
                          </div>
                        </div>
                      )}

                      {/* "O que falta": lista simples do vermelho ao azul, ação ao lado do texto */}
                      {!travado && (
                        <section className="ffc-bloco" aria-labelledby="ffc-falta-t">
                          <h5 className="mb-1" id="ffc-falta-t"><i className="fas fa-list-check me-2 ffc-ciano" aria-hidden="true"></i>O que falta</h5>
                          <p className="text-muted small mb-2">Do obrigatório ao informativo; o botão ao lado resolve o item.</p>
                          {painel.length === 0 && (
                            <div className="ffc-falta-item">
                              <span className="fz-selo ok"><i className="fas fa-circle-check"></i>Tudo certo</span>
                              <span className="ffc-falta-texto">Nada pendente: pronto para {modoRegistro ? 'registrar' : 'a prévia'}.</span>
                            </div>
                          )}
                          {painel.map((p) => {
                            const nv = NIVEL_PAINEL[p.cor] || NIVEL_PAINEL.azul;
                            return (
                              <div key={`${p.item}-${p.acao}`} className="ffc-falta-item">
                                <span className={`fz-selo ${nv.cls}`}><i className={`fas ${nv.ico}`}></i>{nv.rotulo}</span>
                                <span className="ffc-falta-texto">{p.texto}</span>
                                <span className="d-inline-flex gap-1 flex-wrap">{botaoPainel(p)}</span>
                              </div>
                            );
                          })}
                        </section>
                      )}

                      {/* input único dos PDFs: atende o "Subir PDFs" do painel e a área de envio */}
                      {!travado && (
                        <input ref={arquivoRef} type="file" accept="application/pdf,.pdf" multiple className="d-none" disabled={desab}
                          onChange={(e) => subir(e.target.files)} />
                      )}

                      {/* 1 · Documentos do mês */}
                      <Secao {...propsSecao(1)} titulo="Documentos do mês" sub={resumo1}>
                        {!travado && (
                          <div
                            role="button" tabIndex={desab ? -1 : 0}
                            className={`ffc-upload mb-3${arrastando && !desab ? ' arrastando' : ''}${desab ? ' desabilitado' : ''}`}
                            aria-disabled={desab}
                            aria-label="Enviar PDFs dos impostos"
                            onClick={() => { if (!desab) arquivoRef.current?.click(); }}
                            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !desab) { e.preventDefault(); arquivoRef.current?.click(); } }}
                            onDragOver={(e) => { e.preventDefault(); if (!desab) setArrastando(true); }}
                            onDragLeave={() => setArrastando(false)}
                            onDrop={(e) => { e.preventDefault(); setArrastando(false); if (!desab) subir(e.dataTransfer.files); }}
                          >
                            <i className="fas fa-file-arrow-up ffc-upload-ico" aria-hidden="true"></i>
                            <span>
                              <span className="d-block">Arraste os PDFs aqui ou <span className="text-decoration-underline">clique para escolher</span></span>
                              <span className="fz-sub">DARF, DAS, guia de ISS, DCTFWeb, Decweb… até {MAX_DOCS} PDFs de {MAX_MB} MB; a IA lê um por vez.</span>
                            </span>
                          </div>
                        )}
                        {lendo && (
                          <div className="mb-3">
                            <div className="small mb-1"><i className="fas fa-spinner fa-spin me-2 ffc-ciano"></i>
                              Lendo {lendo.atual} de {lendo.total} <span className="text-muted">· {lendo.nome}</span>
                            </div>
                            <div className="progress ffc-barra">
                              <div className="progress-bar" style={{ width: `${Math.round(((lendo.atual - 1) / lendo.total) * 100)}%` }}></div>
                            </div>
                          </div>
                        )}
                        {verResultado && !lendo && (
                          <div className={`alert ${resultadoEnvio.tipo === 'ok' ? 'alert-success' : 'alert-warning'} py-2 small d-flex align-items-center gap-2`} role="status">
                            <i className={`fas ${resultadoEnvio.tipo === 'ok' ? 'fa-circle-check' : 'fa-triangle-exclamation'}`}></i>
                            <span className="flex-grow-1">{resultadoEnvio.texto}{resultadoEnvio.tipo !== 'ok' && docsProblema.length ? ': veja o grupo "Com problema" abaixo.' : ''}</span>
                            <button type="button" className="btn-close btn-sm" aria-label="Fechar aviso" onClick={() => setResultadoEnvio(null)}></button>
                          </div>
                        )}

                        {documentos.length > 0 && (
                          <div className="d-flex gap-2 flex-wrap mb-3" aria-label="Resumo dos PDFs">
                            <span className={`fz-selo ${docsOk.length ? 'ok' : 'neutro'}`}><i className="fas fa-circle-check"></i>{docsOk.length} lido(s)</span>
                            {docsProblema.length > 0 && <span className="fz-selo erro"><i className="fas fa-circle-exclamation"></i>{docsProblema.length} com problema</span>}
                            {docsPendentes.length > 0 && <span className="fz-selo neutro"><i className="fas fa-clock"></i>{docsPendentes.length} aguardando leitura</span>}
                          </div>
                        )}

                        {docsProblema.length > 0 && (
                          <div className="mb-3">
                            <div className="ffc-grupo-head">
                              <button type="button" className="btn btn-sm btn-link ffc-grupo-toggle" aria-expanded={verProblemas} onClick={() => setVerProblemas((v) => !v)}>
                                <i className={`fas fa-chevron-${verProblemas ? 'down' : 'right'} me-2`} aria-hidden="true"></i>Com problema ({docsProblema.length})
                              </button>
                              {!travado && docsOutraEmpresa.length > 0 && (
                                <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={() => setExcluirLote(docsOutraEmpresa)} disabled={desab}>
                                  <i className="fas fa-building-circle-xmark me-1"></i>Remover os de outra empresa ({docsOutraEmpresa.length})
                                </button>
                              )}
                            </div>
                            {verProblemas && tabelaDocs(docsProblema)}
                          </div>
                        )}
                        {docsDemais.length > 0 && (
                          <div>
                            <div className="ffc-grupo-head">
                              <button type="button" className="btn btn-sm btn-link ffc-grupo-toggle" aria-expanded={lidosAbertos}
                                onClick={() => setVerLidos({ chave, aberto: !lidosAbertos })}>
                                <i className={`fas fa-chevron-${lidosAbertos ? 'down' : 'right'} me-2`} aria-hidden="true"></i>
                                Lidos ({docsOk.length}){docsPendentes.length ? ` · aguardando leitura (${docsPendentes.length})` : ''}
                              </button>
                            </div>
                            {lidosAbertos && tabelaDocs(docsDemais)}
                          </div>
                        )}
                      </Secao>

                      {/* 2 · Impostos e faturamento */}
                      <Secao {...propsSecao(2)} titulo="Impostos e faturamento"
                        sub="Valores lidos dos PDFs (ou informados à mão), faturamento do mês e apuração trimestral.">
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                          <h6 className="ffc-sub-titulo m-0">Impostos do mês</h6>
                          {!travado && tributosLivres.length > 0 && !editando && (
                            <button type="button" className="btn btn-sm btn-outline-primary ms-auto" disabled={desab}
                              onClick={() => setEditando({ tributo: tributosLivres[0], valor: '', vencimento: '', novo: true })}>
                              <i className="fas fa-plus me-1"></i>Adicionar tributo
                            </button>
                          )}
                        </div>
                        {conferenciaDctf.length > 0 && (
                          <div className="alert alert-warning py-2 small">
                            <i className="fas fa-triangle-exclamation me-2"></i><b>DCTFWeb × DARF:</b>{' '}
                            {conferenciaDctf.map((c) => `${c.tributo} declarado ${brl(c.declarado)} × DARF ${c.darf == null ? 'não lido' : brl(c.darf)}`).join('; ')}
                          </div>
                        )}
                        <div className="table-responsive mb-4 ffc-tabela">
                          <table className="table table-sm po-table align-middle mb-0">
                            <thead><tr><th>Tributo</th><th className="text-end">Valor</th><th>Vencimento</th><th>Origem</th>{!travado && <th className="text-end">Ações</th>}</tr></thead>
                            <tbody>
                              {impostos.map((i) => (editando && !editando.novo && editando.tributo === i.tributo ? (
                                <tr key={i.tributo}>
                                  <td className="fw-semibold">{ROTULO_TRIBUTO[i.tributo]}</td>
                                  <td><input className="form-control form-control-sm text-end fz-num" value={editando.valor} autoFocus aria-label="Valor"
                                    onChange={(e) => setEditando({ ...editando, valor: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') salvarImposto(); if (e.key === 'Escape') setEditando(null); }} /></td>
                                  <td><input type="date" className="form-control form-control-sm" value={editando.vencimento || ''} aria-label="Vencimento"
                                    onChange={(e) => setEditando({ ...editando, vencimento: e.target.value })} /></td>
                                  <td></td>
                                  <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                                    <button type="button" className="btn btn-sm btn-primary me-1" onClick={salvarImposto} disabled={Boolean(ocupado)}>
                                      <i className={`fas ${ocupado === 'imposto' ? 'fa-spinner fa-spin' : 'fa-check'} me-1`}></i>Salvar
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditando(null)}>Cancelar</button>
                                  </td>
                                </tr>
                              ) : (
                                <tr key={i.tributo}>
                                  <td className="fw-semibold">{ROTULO_TRIBUTO[i.tributo]}</td>
                                  <td className="text-end fz-num">{brl(i.valor)}</td>
                                  <td className="fz-num">{dataBr(i.vencimento)}</td>
                                  <td className="small">
                                    {i.editado ? <span className="fz-selo neutro"><i className="fas fa-pen"></i>editado</span>
                                      : <span className="d-inline-block text-truncate ffc-nome" title={nomeDoc[i.documento_id] || ''}>{nomeDoc[i.documento_id] || '—'}</span>}
                                  </td>
                                  {!travado && (
                                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                                      <button type="button" className="btn btn-sm btn-outline-secondary me-1" disabled={desab || Boolean(editando)}
                                        onClick={() => setEditando({ tributo: i.tributo, valor: valorTexto(i.valor), vencimento: i.vencimento ? String(i.vencimento).slice(0, 10) : '' })}>
                                        <i className="fas fa-pen me-1"></i>Editar
                                      </button>
                                      <button type="button" className="btn btn-sm btn-outline-danger" disabled={desab} onClick={() => removerImposto(i.tributo)}>
                                        <i className={`fas ${ocupado === `rm-${i.tributo}` ? 'fa-spinner fa-spin' : 'fa-trash'} me-1`}></i>Remover
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              )))}
                              {editando?.novo && (
                                <tr>
                                  <td><select className="form-select form-select-sm" aria-label="Tributo" value={editando.tributo} onChange={(e) => setEditando({ ...editando, tributo: e.target.value })}>
                                    {tributosLivres.map((t) => <option key={t} value={t}>{ROTULO_TRIBUTO[t]}</option>)}
                                  </select></td>
                                  <td><input className="form-control form-control-sm text-end fz-num" placeholder="0,00" value={editando.valor} autoFocus aria-label="Valor"
                                    onChange={(e) => setEditando({ ...editando, valor: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') salvarImposto(); if (e.key === 'Escape') setEditando(null); }} /></td>
                                  <td><input type="date" className="form-control form-control-sm" value={editando.vencimento} aria-label="Vencimento"
                                    onChange={(e) => setEditando({ ...editando, vencimento: e.target.value })} /></td>
                                  <td className="small text-muted">manual</td>
                                  <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                                    <button type="button" className="btn btn-sm btn-primary me-1" onClick={salvarImposto} disabled={Boolean(ocupado)}>
                                      <i className={`fas ${ocupado === 'imposto' ? 'fa-spinner fa-spin' : 'fa-check'} me-1`}></i>Salvar
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditando(null)}>Cancelar</button>
                                  </td>
                                </tr>
                              )}
                              {!impostos.length && !editando?.novo && <tr><td colSpan={travado ? 4 : 5} className="text-center text-muted py-3">Nenhum imposto: suba os PDFs ou adicione à mão.</td></tr>}
                            </tbody>
                          </table>
                        </div>

                        <h6 className="ffc-sub-titulo mb-2">Faturamento</h6>
                        <div className="ffc-stats mb-3">
                          <div className={`ffc-stat${ctx.faturamento ? '' : ' falta'}`}>
                            <div className="ffc-stat-rotulo"><i className="fas fa-sack-dollar"></i>Faturamento do mês</div>
                            <div className="ffc-stat-valor fz-num">{brl(ctx.faturamento)}</div>
                            <div className="ffc-stat-sub">{FONTE_FAT[ctx.fonte_faturamento] || 'sem fonte'}{ctx.faturamento_anterior != null ? ` · ${ctx.mes_anterior_rotulo}: ${brl(ctx.faturamento_anterior)}` : ''}</div>
                          </div>
                          <div className="ffc-stat">
                            <div className="ffc-stat-rotulo"><i className="fas fa-file-invoice"></i>NFS-e emitidas</div>
                            <div className="ffc-stat-valor fz-num">{ctx.qtd_notas || 0}</div>
                            <div className="ffc-stat-sub">ativas na competência (Monitor)</div>
                          </div>
                          <div className="ffc-stat">
                            <div className="ffc-stat-rotulo"><i className="fas fa-landmark"></i>Receita Bruta (Decweb)</div>
                            <div className="ffc-stat-valor fz-num">{brl(ctx.faturamento_decweb)}</div>
                            <div className="ffc-stat-sub">{ctx.faturamento_decweb != null && ctx.faturamento != null && Math.abs(Number(ctx.faturamento) - Number(ctx.faturamento_decweb)) > 0.01
                              ? 'diferente do faturamento usado' : 'só confere'}</div>
                          </div>
                        </div>
                        {!travado && (
                          <div className="d-flex gap-2 flex-wrap align-items-center mb-3">
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => notas('atualizar')} disabled={desab}>
                              <i className={`fas ${ocupado === 'notas-atualizar' ? 'fa-spinner fa-spin' : 'fa-rotate'} me-1`}></i>Atualizar notas
                            </button>
                            {fatManual == null ? (
                              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={desab}
                                onClick={() => { setFatManual(ctx.fonte_faturamento === 'manual' ? valorTexto(ctx.faturamento) : ''); focar(fatRef); }}>
                                <i className="fas fa-keyboard me-1"></i>Informar manualmente
                              </button>
                            ) : (
                              <>
                                <input ref={fatRef} className="form-control form-control-sm text-end fz-num" style={{ maxWidth: 180 }} placeholder="0,00" aria-label="Faturamento manual"
                                  value={fatManual} onChange={(e) => setFatManual(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Escape') setFatManual(null); }} />
                                <button type="button" className="btn btn-sm btn-primary" disabled={Boolean(ocupado)}
                                  onClick={() => salvar({ faturamento: fatManual }, 'faturamento').then(() => { if (montado.current) setFatManual(null); })}>
                                  <i className={`fas ${ocupado === 'faturamento' ? 'fa-spinner fa-spin' : 'fa-check'} me-1`}></i>Salvar
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setFatManual(null)}>Cancelar</button>
                                <span className="fz-sub">vazio = volta ao Monitor/Decweb</span>
                              </>
                            )}
                          </div>
                        )}

                        {ctx.trimestre && (
                          <div className="mt-4">
                            <h6 className="ffc-sub-titulo mb-2">Apuração trimestral ({(ctx.trimestre.meses || []).map(([a, m]) => compBr(compDe(a, m))).join(', ')})</h6>
                            {!ctx.trimestre.completo ? (
                              <div className="small text-muted">
                                Faltam meses gravados ({(ctx.trimestre.faltando || []).map(([a, m]) => compBr(compDe(a, m))).join(', ')}): o bloco trimestral não entra no e-mail e não trava o envio.
                              </div>
                            ) : (
                              <>
                                <div className="table-responsive">
                                  <table className="table table-sm po-table align-middle mb-2">
                                    <thead><tr><th></th><th className="text-end">Calculado (regra)</th><th className="text-end">Lido (DARF)</th><th className="text-end">Diferença</th></tr></thead>
                                    <tbody>
                                      <tr><td className="small">Faturamento do trimestre</td><td className="text-end fz-num">{brl(ctx.trimestre.calculado?.faturamento)}</td><td></td><td></td></tr>
                                      <tr><td className="small">Base presumida</td><td className="text-end fz-num">{brl(ctx.trimestre.calculado?.base)}</td><td></td><td></td></tr>
                                      {['irpj', 'csll'].map((k) => {
                                        const div = (ctx.trimestre.divergencias || []).find((d) => d.tributo === k.toUpperCase());
                                        return (
                                          <tr key={k} className={div ? 'table-danger' : ''}>
                                            <td className="small fw-semibold">{k.toUpperCase()}</td>
                                            <td className="text-end fz-num">{brl(ctx.trimestre.calculado?.[k])}</td>
                                            <td className="text-end fz-num">{brl(ctx.trimestre.lido?.[k])}</td>
                                            <td className="text-end fz-num">{div ? brl(div.diferenca) : <span className="fz-selo ok"><i className="fas fa-check"></i>confere</span>}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                                {(ctx.trimestre.divergencias || []).length > 0 && (
                                  <>
                                    <label htmlFor="ffc-justificativa" className="form-label small fw-bold mb-1">Justificativa da divergência <span className="text-danger">(obrigatória para enviar)</span></label>
                                    <textarea id="ffc-justificativa" ref={justRef} className="form-control form-control-sm mb-2 ffc-texto" rows={2} value={justificativa} disabled={travado}
                                      onChange={(e) => setJustificativa(e.target.value)} placeholder="Ex.: DARF recolhido com compensação de saldo negativo" />
                                    {!travado && (
                                      <button type="button" className="btn btn-sm btn-primary" disabled={desab || justificativa === (estado.justificativa_trimestral || '')}
                                        onClick={() => salvar({ justificativa_trimestral: justificativa }, 'justificativa')}>
                                        <i className={`fas ${ocupado === 'justificativa' ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-1`}></i>Salvar justificativa
                                      </button>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </Secao>

                      {/* 3 · Destinatários */}
                      {!modoRegistro && (
                        <Secao {...propsSecao(3)} titulo="Destinatários" sub={resumo3}>
                          {(ctx.destinatarios || []).map((d) => (
                            <div key={d.email} className="ffc-dest">
                              <input type="checkbox" className="form-check-input m-0" id={`dest-${d.email}`} checked={d.marcado ?? true}
                                disabled={desab} onChange={() => alternarDestinatario(d.email)} />
                              <label htmlFor={`dest-${d.email}`} className="small flex-grow-1 mb-0" style={{ overflowWrap: 'anywhere' }}>
                                {d.nome ? <><b>{d.nome}</b> · </> : null}<span className="fz-mono">{d.email}</span>
                              </label>
                              {!travado && (
                                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={desab} onClick={() => removerEmail(d.email)}>
                                  <i className="fas fa-xmark me-1"></i>Tirar
                                </button>
                              )}
                            </div>
                          ))}
                          {!(ctx.destinatarios || []).length && <div className="text-muted small mb-1">Nenhum contato no Acessórias: inclua o e-mail.</div>}
                          {!travado && (
                            <div className="d-flex gap-2 mt-3 flex-wrap">
                              <input className="form-control form-control-sm" style={{ maxWidth: 160 }} placeholder="Nome" aria-label="Nome do destinatário" value={novoEmail.nome}
                                onChange={(e) => setNovoEmail({ ...novoEmail, nome: e.target.value })} />
                              <input ref={emailRef} type="email" className="form-control form-control-sm" style={{ maxWidth: 240 }} placeholder="email@cliente.com.br" aria-label="E-mail do destinatário"
                                value={novoEmail.email} onChange={(e) => setNovoEmail({ ...novoEmail, email: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Enter') incluirEmail(); }} />
                              <button type="button" className="btn btn-sm btn-outline-primary" onClick={incluirEmail} disabled={desab || !novoEmail.email.trim()}>
                                <i className={`fas ${ocupado === 'destinatarios' ? 'fa-spinner fa-spin' : 'fa-plus'} me-1`}></i>Incluir e-mail
                              </button>
                            </div>
                          )}
                        </Secao>
                      )}

                      {/* 4 · Mensagem do assessor (opcional) */}
                      {!modoRegistro && (
                        <Secao n={4} titulo="Mensagem do assessor" sub="Opcional: texto que aparece no e-mail, logo depois da abertura.">
                          <label htmlFor="ffc-mensagem" className="visually-hidden">Mensagem do assessor</label>
                          <textarea id="ffc-mensagem" className="form-control form-control-sm mb-2 ffc-texto" rows={4} value={mensagem} disabled={travado} maxLength={4000}
                            onChange={(e) => setMensagem(e.target.value)} placeholder="Ex.: Neste mês o ISS subiu por causa da nota de consultoria emitida no dia 15." />
                          {!travado && (
                            <div className="d-flex flex-wrap gap-2">
                              <button type="button" className="btn btn-sm btn-primary" disabled={desab || mensagem === (ctx.mensagem || '')}
                                onClick={() => salvar({ mensagem }, 'mensagem')}>
                                <i className={`fas ${ocupado === 'mensagem' ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-1`}></i>Salvar mensagem
                              </button>
                              <button type="button" className="btn btn-sm btn-outline-primary" disabled={desab} onClick={gerarMensagemIa}
                                title="A IA escreve um rascunho com os números deste fechamento (pode levar 1 a 2 minutos)">
                                <i className={`fas ${ocupado === 'mensagem-ia' ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'} me-1`}></i>
                                {ocupado === 'mensagem-ia' ? 'Gerando…' : 'Gerar rascunho com IA'}
                              </button>
                            </div>
                          )}
                        </Secao>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ------------------------------------------- coluna da direita: prévia e ações */}
          <div className="col-lg-5">
            <div className="ffc-lateral">
              <div className="card shadow-sm ffc-card">
                <div className="card-header bg-light ffc-card-head">
                  <h5 className="mb-0">
                    <i className={`fas ${modoRegistro ? 'fa-box-archive' : 'fa-envelope-open-text'} me-2 ffc-ciano`} aria-hidden="true"></i>
                    {modoRegistro ? 'Registro do mês' : 'Prévia do e-mail'}
                  </h5>
                  {!modoRegistro && pv && previaViva.carregando && (
                    <span className="fz-sub ms-auto" role="status"><i className="fas fa-spinner fa-spin me-1"></i>atualizando…</span>
                  )}
                </div>
                <div className="card-body">
                  {modoRegistro ? (
                    <div className="ffc-registro">
                      <i className="fas fa-box-archive ffc-registro-ico" aria-hidden="true"></i>
                      <p className="mb-2"><b>Modo registro:</b> este mês será só gravado para comparação; nada é enviado ao cliente.</p>
                      <p className="small text-muted mb-0">
                        Competência {compBr(comp)}: suba os PDFs, confira impostos e faturamento e clique em "Registrar sem enviar".
                        {estado.status === 'registrado' && ' Este mês já está registrado.'}
                      </p>
                    </div>
                  ) : (
                    <>
                      {pv && (
                        <dl className="ffc-previa-info">
                          <dt>Para</dt>
                          <dd>{pv.para?.length ? pv.para.join(', ') : <span className="text-danger">nenhum destinatário marcado</span>}</dd>
                          <dt>Assunto</dt><dd>{pv.assunto}</dd>
                          <dt>Tamanho</dt>
                          <dd className="fz-num">{String(pv.tamanho_kb).replace('.', ',')} KB{pv.tamanho_kb > 98 && <span className="text-danger"> · acima do alvo de 98 KB (Gmail corta em ~102 KB)</span>}</dd>
                        </dl>
                      )}
                      {pv && !pv.conta_conectada && (
                        <div className="alert alert-warning py-2 small"><i className="fas fa-triangle-exclamation me-2"></i>
                          A conta central (fiscal@) não está conectada: autentique em <Link to="/Fiscal/Configuracoes">Fiscal → Configurações</Link> para enviar.
                        </div>
                      )}
                      <div className="ffc-previa-quadro">
                        {pv ? (
                          <iframe title="Prévia do e-mail ao cliente" sandbox="allow-same-origin" srcDoc={pv.html} className="ffc-previa-frame" />
                        ) : (
                          <div className="ffc-previa-vazia" role="status">
                            {previaViva.erro ? (
                              <>
                                <span><i className="fas fa-triangle-exclamation me-2 text-warning"></i>{previaViva.erro}</span>
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setTentativaPrevia((n) => n + 1)}>
                                  <i className="fas fa-rotate me-1"></i>Tentar de novo
                                </button>
                              </>
                            ) : (
                              <span><i className="fas fa-spinner fa-spin me-2 ffc-ciano"></i>Montando a prévia do e-mail…</span>
                            )}
                          </div>
                        )}
                        {pv && previaViva.carregando && <div className="ffc-previa-veu" aria-hidden="true"></div>}
                      </div>
                    </>
                  )}
                </div>
                <div className="card-footer ffc-acoes">
                  {ctx.bloqueia_envio && !modoRegistro && !travado && (
                    <div className="alert alert-danger py-2 small mb-3" role="alert">
                      <i className="fas fa-lock me-2"></i><b>Envio bloqueado.</b>
                      {pv?.bloqueia_envio && (pv.pendencias || []).length ? (
                        <ul className="mb-0 mt-1">{pv.pendencias.map((p) => <li key={p}>{p}</li>)}</ul>
                      ) : <> Resolva os itens obrigatórios de "O que falta" para enviar.</>}
                    </div>
                  )}
                  {podeEnviar && !conferido && (
                    <div className="fz-sub mb-2 text-end">
                      <i className="fas fa-calculator me-1"></i>O envio exige a{' '}
                      <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setAba('calculos')}>conferência dos cálculos</button>.
                    </div>
                  )}
                  <div className="ffc-acoes-botoes">
                    {modoRegistro && (
                      <button type="button" className="btn btn-outline-secondary me-auto" onClick={() => irRegistro(competencia)} disabled={Boolean(lendo)}>
                        <i className="fas fa-arrow-left me-2"></i>Voltar para {compBr(competencia)}
                      </button>
                    )}
                    {botaoReabrir}
                    {botaoRegistrar}
                    {!modoRegistro && !travado && (
                      <button type="button" className="btn btn-primary" onClick={abrirPrevia} disabled={desab}>
                        <i className="fas fa-envelope-open-text me-2"></i>Abrir prévia e enviar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {previa && (
        <Modal icone="fa-envelope-open-text" titulo="Prévia do fechamento ao cliente" grande
          sub={`${ctx?.empresa?.cliente || ''} · ${ctx?.mes_rotulo || compBr(comp)} — confira antes de enviar`}
          onFechar={() => { if (ocupado !== 'enviar') { setPrevia(null); setConfirmarEnvio(false); } }}
          rodape={previa.dados && confirmarEnvio ? (
            <>
              <span className="me-auto small fw-semibold">Enviar para {previa.dados.para.length} destinatário(s)?</span>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmarEnvio(false)} disabled={ocupado === 'enviar'}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={enviar} disabled={ocupado === 'enviar' || !conferido}>
                <i className={`fas ${ocupado === 'enviar' ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>Confirmar envio
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setPrevia(null)}><i className="fas fa-times me-2"></i>Fechar</button>
              {previa.dados && previa.dados.status !== 'enviado' && (
                <button type="button" className="btn btn-primary" onClick={() => setConfirmarEnvio(true)}
                  disabled={previa.dados.bloqueia_envio || !previa.dados.conta_conectada || !previa.dados.para.length || !conferido}>
                  <i className="fas fa-paper-plane me-2"></i>Enviar
                </button>
              )}
            </>
          )}>
          {previa.carregando ? (
            <div className="text-center text-muted py-4"><i className="fas fa-spinner fa-spin me-2"></i>Montando a prévia…</div>
          ) : (
            <>
              <dl className="row small mb-2">
                <dt className="col-sm-2">De</dt><dd className="col-sm-10">{previa.dados.de}</dd>
                <dt className="col-sm-2">Para</dt>
                <dd className="col-sm-10">{previa.dados.para.length ? previa.dados.para.join(', ') : <span className="text-danger">nenhum destinatário marcado</span>}</dd>
                <dt className="col-sm-2">Assunto</dt><dd className="col-sm-10">{previa.dados.assunto}</dd>
                <dt className="col-sm-2">Tamanho</dt>
                <dd className="col-sm-10 fz-num">{String(previa.dados.tamanho_kb).replace('.', ',')} KB{previa.dados.tamanho_kb > 98 && <span className="text-danger"> · acima do alvo de 98 KB (Gmail corta em ~102 KB)</span>}</dd>
              </dl>
              {!previa.dados.conta_conectada && (
                <div className="alert alert-warning py-2 small"><i className="fas fa-triangle-exclamation me-2"></i>
                  A conta central (fiscal@) não está conectada: autentique em <Link to="/Fiscal/Configuracoes">Fiscal → Configurações</Link> para enviar.
                </div>
              )}
              {!conferido && previa.dados.status !== 'enviado' && (
                <div className="alert alert-warning py-2 small d-flex flex-wrap align-items-center gap-2">
                  <span><i className="fas fa-calculator me-2"></i>Antes de enviar, marque "Conferi a base de cálculo e os valores" na aba Conferência dos cálculos.</span>
                  <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={() => { setPrevia(null); setConfirmarEnvio(false); setAba('calculos'); }}>
                    <i className="fas fa-calculator me-1"></i>Ir para a conferência
                  </button>
                </div>
              )}
              {previa.dados.bloqueia_envio && (
                <div className="alert alert-danger py-2 small"><i className="fas fa-lock me-2"></i>Envio bloqueado:
                  <ul className="mb-0 mt-1">{(previa.dados.pendencias || []).map((p) => <li key={p}>{p}</li>)}</ul>
                </div>
              )}
              <iframe title="Prévia do e-mail" sandbox="" srcDoc={previa.dados.html} className="fz-previa-email" />
            </>
          )}
        </Modal>
      )}

      {excluir && (
        <Modal icone="fa-trash" titulo="Excluir PDF" sub={excluir.nome} onFechar={() => setExcluir(null)}
          rodape={(
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setExcluir(null)}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={confirmarExclusao} disabled={Boolean(ocupado)}>
                <i className={`fas ${ocupado.startsWith('excluir') ? 'fa-spinner fa-spin' : 'fa-trash'} me-2`}></i>Excluir
              </button>
            </>
          )}>
          <p className="mb-0 small">O PDF sai do bucket e os tributos lidos dele saem do mês. Valores editados à mão são mantidos.</p>
        </Modal>
      )}

      {excluirLote && (
        <Modal icone="fa-building-circle-xmark" titulo="Remover PDFs de outra empresa" sub={`${excluirLote.length} PDF(s) · ${ctx?.empresa?.cliente || ''}`}
          onFechar={() => { if (ocupado !== 'excluir-lote') setExcluirLote(null); }}
          rodape={(
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setExcluirLote(null)} disabled={ocupado === 'excluir-lote'}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={confirmarExclusaoLote} disabled={Boolean(ocupado)}>
                <i className={`fas ${ocupado === 'excluir-lote' ? 'fa-spinner fa-spin' : 'fa-trash'} me-2`}></i>Remover {excluirLote.length} PDF(s)
              </button>
            </>
          )}>
          <p className="small">Estes PDFs têm o CNPJ de outra empresa e não entram no fechamento. Eles saem do bucket:</p>
          <ul className="small mb-0 ffc-lista-lote">
            {excluirLote.map((d) => (
              <li key={d.id}><span className="fw-semibold">{d.nome}</span> <span className="text-muted">· {problemaDoc(d)}</span></li>
            ))}
          </ul>
        </Modal>
      )}

      {confirmarHistorico && (() => {
        const h = confirmarHistorico;
        const erroH = h.todo ? '' : erroPeriodo(h.de, h.ate);
        const fechar = () => setConfirmarHistorico(null);
        const mudar = (campo) => (e) => setConfirmarHistorico((x) => ({ ...x, [campo]: campo === 'todo' ? e.target.checked : e.target.value }));
        return (
          <Modal icone="fa-clock-rotate-left" titulo="Buscar histórico no ADN" sub={ctx?.empresa?.cliente || ''}
            onFechar={fechar}
            rodape={(
              <>
                <button type="button" className="btn btn-secondary" onClick={fechar}>Cancelar</button>
                <button type="button" className="btn btn-primary" disabled={desab || Boolean(erroH)}
                  onClick={() => { fechar(); notas('historico', h.todo ? null : { inicio: h.de, fim: h.ate }); }}>
                  <i className="fas fa-cloud-arrow-down me-2"></i>Buscar histórico
                </button>
              </>
            )}>
            <div className="row g-2 mb-2">
              <div className="col-6">
                <label className="form-label small fw-bold mb-1" htmlFor="fch-hist-de">De</label>
                <input id="fch-hist-de" type="month" className="form-control form-control-sm" value={h.de || ''}
                  max={mesAtual()} disabled={h.todo} onChange={mudar('de')} />
              </div>
              <div className="col-6">
                <label className="form-label small fw-bold mb-1" htmlFor="fch-hist-ate">Até</label>
                <input id="fch-hist-ate" type="month" className="form-control form-control-sm" value={h.ate || ''}
                  max={mesAtual()} disabled={h.todo} onChange={mudar('ate')} />
              </div>
            </div>
            {erroH && <div className="small text-danger mb-2" role="alert"><i className="fas fa-circle-exclamation me-1"></i>{erroH}</div>}
            <div className="form-check mb-2">
              <input id="fch-hist-todo" type="checkbox" className="form-check-input" checked={h.todo} onChange={mudar('todo')} />
              <label className="form-check-label small" htmlFor="fch-hist-todo">Todo o histórico (desde o início)</label>
            </div>
            <p className="small mb-2">
              {h.todo
                ? 'Baixa todas as notas da empresa no ADN desde o início (pode levar de minutos a horas, conforme o volume).'
                : 'O ADN não filtra por mês: o Saber localiza o início do período na sequência de notas, grava só as notas desses meses e para depois do fim (com 2 meses de folga para notas emitidas com atraso).'}
              {' '}Notas já existentes são ignoradas e notas antigas não geram alerta de retroativa.
            </p>
            <p className="small text-muted mb-0">Use quando o mês é anterior ao início do download automático. Não feche a página até terminar.</p>
          </Modal>
        );
      })()}

      {reabrir && (
        <Modal icone="fa-lock-open" titulo="Reabrir fechamento" sub={`${ctx?.empresa?.cliente || ''} · ${ctx?.mes_rotulo || ''}`} onFechar={() => setReabrir(null)}
          rodape={(
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setReabrir(null)}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={confirmarReabertura} disabled={Boolean(ocupado) || reabrir.motivo.trim().length < 5}>
                <i className={`fas ${ocupado === 'reabrir' ? 'fa-spinner fa-spin' : 'fa-lock-open'} me-2`}></i>Reabrir
              </button>
            </>
          )}>
          <label className="form-label small fw-bold mb-1">Motivo (fica no histórico)</label>
          <textarea className="form-control" rows={3} autoFocus value={reabrir.motivo}
            onChange={(e) => setReabrir({ motivo: e.target.value })} placeholder="Ex.: cliente pediu correção do ISS" />
          {estado.status === 'enviado' && <div className="fz-sub mt-2">O e-mail já enviado não é desfeito: depois de corrigir, envie de novo pela prévia.</div>}
        </Modal>
      )}
    </section>
  );
}
